import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { canWrite, canDelete, WRITE_PERMISSION_DENIED, DELETE_PERMISSION_DENIED } from '@/services/permissions';
import { getAgentSalespersonId } from '@/services/agent';
import { recordAudit } from '@/services/audit';

export interface Commission {
  id: string;
  salespersonId: string;
  saleId: string;
  salespersonName: string;
  saleNumber: string;
  saleAmount: number;
  commissionRate: number;
  commissionAmount: number;
  isPaid: boolean;
  paidAt: string;
  createdAt: string;
  // Total efectivamente abonado (suma de commission_payments) y lo pendiente.
  paidAmount: number;
  pendingAmount: number;
}

export interface CommissionsResult {
  data: Commission[];
}

export async function fetchCommissions(): Promise<CommissionsResult> {
  if (!isSupabaseConfigured || !supabase) {
    return { data: [] };
  }

  try {
    const [commissionsRes, salespersonsRes, salesRes] = await Promise.all([
      supabase.from('commissions').select('*'),
      supabase.from('salespersons').select('id, full_name'),
      supabase.from('sales').select('id, sale_number'),
    ]);

    if (commissionsRes.error) throw new Error(commissionsRes.error.message);
    if (salespersonsRes.error) throw new Error(salespersonsRes.error.message);
    if (salesRes.error) throw new Error(salesRes.error.message);

    // Abonos parciales (tabla commission_payments). Se consulta de forma
    // tolerante: si la tabla aún no existe, el "pagado" se deriva del flag.
    let paymentsByCommission = new Map<string, number>();
    try {
      const { data: payments } = await supabase.from('commission_payments').select('commission_id, amount');
      ((payments ?? []) as Array<Record<string, any>>).forEach((p) => {
        const prev = paymentsByCommission.get(p.commission_id) ?? 0;
        paymentsByCommission.set(p.commission_id, prev + Number(p.amount ?? 0));
      });
    } catch {
      // Tabla de abonos aún no creada: se usa el flag is_paid como respaldo.
      paymentsByCommission = new Map();
    }

    const spMap = new Map(
      ((salespersonsRes.data ?? []) as Array<Record<string, any>>).map((s) => [s.id, s.full_name]),
    );
    const saleMap = new Map(
      ((salesRes.data ?? []) as Array<Record<string, any>>).map((s) => [s.id, s.sale_number]),
    );

    // Filtro por agente: un agente de ventas solo ve sus propias comisiones.
    const agentSalespersonId = await getAgentSalespersonId();
    let rows = (commissionsRes.data ?? []) as Array<Record<string, any>>;
    if (agentSalespersonId) {
      rows = rows.filter((c) => c.salesperson_id === agentSalespersonId);
    }

    const mapped: Commission[] = rows
      .map((c) => {
        const amount = Number(c.commission_amount);
        const sumPayments = paymentsByCommission.get(c.id) ?? 0;
        const paidAmount = sumPayments > 0 ? sumPayments : c.is_paid ? amount : 0;
        return {
          id: c.id,
          salespersonId: c.salesperson_id ?? '',
          saleId: c.sale_id ?? '',
          salespersonName: spMap.get(c.salesperson_id) ?? '',
          saleNumber: saleMap.get(c.sale_id) ?? '',
          saleAmount: Number(c.sale_amount),
          commissionRate: Number(c.commission_rate),
          commissionAmount: amount,
          isPaid: c.is_paid ?? false,
          paidAt: c.paid_at ?? '',
          createdAt: c.created_at ?? '',
          paidAmount,
          pendingAmount: Math.max(0, amount - paidAmount),
        };
      })
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));

    return { data: mapped };
  } catch (err) {
    console.error('[commissions] connection failed:', err);
    throw err;
  }
}

export interface CommissionInput {
  salespersonId: string;
  saleId: string;
  saleAmount: number;
  commissionRate: number;
}

// Crea una comisión calculando commission_amount = sale_amount * rate.
export async function saveCommission(
  input: CommissionInput,
): Promise<{ data: Commission | null; error: string | null }> {
  if (!(await canWrite('commissions'))) {
    return { data: null, error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { data: null, error: 'Supabase no está configurado.' };
  }

  const commissionAmount = Number(input.saleAmount) * (Number(input.commissionRate) / 100);

  const payload = {
    salesperson_id: input.salespersonId,
    sale_id: input.saleId,
    sale_amount: Number(input.saleAmount),
    commission_rate: Number(input.commissionRate),
    commission_amount: commissionAmount,
    is_paid: false,
  };

  try {
    const { data, error } = await supabase.from('commissions').insert(payload).select('*');
    if (error) return { data: null, error: error.message };
    const row = data?.[0] as Record<string, any> | undefined;
    if (!row) return { data: null, error: 'No se pudo leer la comisión guardada.' };
    recordAudit({
      entityType: 'commissions',
      entityId: row.id,
      action: 'CREATE',
      newValue: `$${commissionAmount.toFixed(2)}`,
    });
    return {
      data: {
        id: row.id,
        salespersonName: '',
        saleNumber: '',
        saleAmount: Number(row.sale_amount),
        commissionRate: Number(row.commission_rate),
        commissionAmount: Number(row.commission_amount),
        isPaid: row.is_paid ?? false,
        paidAt: row.paid_at ?? '',
        createdAt: row.created_at ?? '',
      },
      error: null,
    };
  } catch (err) {
    console.warn('[commissions] save failed:', err);
    return { data: null, error: 'Error de conexión al guardar la comisión.' };
  }
}

// Actualiza una comisión existente: vendedor, venta, monto y porcentaje.
export async function updateCommission(
  id: string,
  input: CommissionInput,
): Promise<{ error: string | null }> {
  if (!(await canWrite('commissions'))) {
    return { error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { error: 'Supabase no está configurado.' };
  }

  const commissionAmount = Number(input.saleAmount) * (Number(input.commissionRate) / 100);

  const payload = {
    salesperson_id: input.salespersonId,
    sale_id: input.saleId,
    sale_amount: Number(input.saleAmount),
    commission_rate: Number(input.commissionRate),
    commission_amount: commissionAmount,
  };

  try {
    const { error } = await supabase.from('commissions').update(payload).eq('id', id);
    if (!error) {
      recordAudit({
        entityType: 'commissions',
        entityId: id,
        action: 'UPDATE',
        newValue: `$${commissionAmount.toFixed(2)}`,
      });
    }
    return { error: error?.message ?? null };
  } catch (err) {
    console.warn('[commissions] update failed:', err);
    return { error: 'Error de conexión al actualizar la comisión.' };
  }
}

// Marca una comisión como pagada (o la revierte a pendiente).
export async function setCommissionPaid(
  id: string,
  isPaid: boolean,
): Promise<{ error: string | null }> {
  if (!(await canWrite('commissions'))) {
    return { error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { error: 'Supabase no está configurado.' };
  }

  const patch = isPaid ? { is_paid: true, paid_at: new Date().toISOString() } : { is_paid: false, paid_at: null };
  try {
    const { error } = await supabase.from('commissions').update(patch).eq('id', id);
    if (!error) {
      recordAudit({
        entityType: 'commissions',
        entityId: id,
        action: 'UPDATE',
        fieldName: 'is_paid',
        oldValue: String(!isPaid),
        newValue: String(isPaid),
      });
    }
    return { error: error?.message ?? null };
  } catch (err) {
    console.warn('[commissions] setPaid failed:', err);
    return { error: 'Error de conexión al actualizar la comisión.' };
  }
}

// Marca como pagadas TODAS las comisiones pendientes de un vendedor de una
// sola vez (pago en lote). Devuelve cuántas comisiones se liquidaron.
export async function markSalespersonCommissionsPaid(
  salespersonId: string,
  commissionIds?: string[],
): Promise<{ error: string | null; count: number }> {
  if (!(await canWrite('commissions'))) {
    return { error: WRITE_PERMISSION_DENIED, count: 0 };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { error: 'Supabase no está configurado.', count: 0 };
  }

  try {
    let query = supabase
      .from('commissions')
      .update({ is_paid: true, paid_at: new Date().toISOString() })
      .eq('salesperson_id', salespersonId)
      .or('is_paid.eq.false,is_paid.is.null');
    if (commissionIds && commissionIds.length > 0) {
      query = query.in('id', commissionIds);
    }
    const { data, error } = await query.select('id');
    if (error) return { error: error.message, count: 0 };

    const count = (data ?? []).length;
    if (count > 0) {
      recordAudit({
        entityType: 'commissions',
        entityId: salespersonId,
        action: 'UPDATE',
        fieldName: 'bulk_pay',
        newValue: `${count} comisiones marcadas como pagadas`,
      });
    }
    return { error: null, count };
  } catch (err) {
    console.warn('[commissions] bulk pay failed:', err);
    return { error: 'Error de conexión al pagar las comisiones.', count: 0 };
  }
}

// Elimina una comisión existente.
export async function deleteCommission(id: string): Promise<{ error: string | null }> {
  if (!(await canDelete('commissions'))) {
    return { error: DELETE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { error: 'Supabase no está configurado.' };
  }

  try {
    const { error } = await supabase.from('commissions').delete().eq('id', id);
    if (!error) {
      recordAudit({
        entityType: 'commissions',
        entityId: id,
        action: 'DELETE',
      });
    }
    return { error: error?.message ?? null };
  } catch (err) {
    console.warn('[commissions] delete failed:', err);
    return { error: 'Error de conexión al eliminar la comisión.' };
  }
}

// --- Abonos / pagos de comisión (sistema de abonos) ---
// Espejo del historial de valores donados: permite registrar pagos parciales
// a un vendedor hasta cubrir el total de su comisión.

export interface CommissionPayment {
  id: string;
  commissionId: string;
  amount: number;
  paidAt: string;
  notes: string;
  createdAt: string;
}

export async function fetchCommissionPayments(commissionId: string): Promise<CommissionPayment[]> {
  if (!isSupabaseConfigured || !supabase) return [];
  const { data, error } = await supabase
    .from('commission_payments')
    .select('*')
    .eq('commission_id', commissionId)
    .order('paid_at', { ascending: false, nullsFirst: false });
  if (error) {
    console.warn('[commissions] fetchCommissionPayments error', error.message);
    return [];
  }
  return ((data ?? []) as Array<Record<string, any>>).map((e) => ({
    id: e.id,
    commissionId: e.commission_id,
    amount: Number(e.amount),
    paidAt: e.paid_at ?? '',
    notes: e.notes ?? '',
    createdAt: e.created_at ?? '',
  }));
}

export async function addCommissionPayment(input: {
  commissionId: string;
  amount: number;
  paidAt: string;
  notes: string;
}): Promise<{ error: string | null }> {
  if (!(await canWrite('commissions'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };

  const { error } = await supabase.from('commission_payments').insert({
    commission_id: input.commissionId,
    amount: Number(input.amount),
    paid_at: input.paidAt || null,
    notes: input.notes.trim() || null,
  });
  if (error) return { error: error.message };

  // Si con este abono ya se cubre el total, se marca la comisión como pagada.
  try {
    const { data: payments } = await supabase
      .from('commission_payments')
      .select('amount')
      .eq('commission_id', input.commissionId);
    const { data: comm } = await supabase
      .from('commissions')
      .select('commission_amount')
      .eq('id', input.commissionId)
      .maybeSingle();
    const total = Number((comm as Record<string, any> | null)?.commission_amount ?? 0);
    const paid = ((payments ?? []) as Array<Record<string, any>>).reduce(
      (s, p) => s + Number(p.amount ?? 0),
      0,
    );
    if (total > 0 && paid >= total - 0.001) {
      await supabase
        .from('commissions')
        .update({ is_paid: true, paid_at: new Date().toISOString() })
        .eq('id', input.commissionId);
    }
  } catch {
    /* no-op */
  }

  recordAudit({
    entityType: 'commissions',
    entityId: input.commissionId,
    action: 'UPDATE',
    fieldName: 'payment',
    newValue: `$${input.amount}`,
  });
  return { error: null };
}

export async function deleteCommissionPayment(
  id: string,
  commissionId: string,
): Promise<{ error: string | null }> {
  if (!(await canWrite('commissions'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };

  const { error } = await supabase.from('commission_payments').delete().eq('id', id);
  if (error) return { error: error.message };

  recordAudit({
    entityType: 'commissions',
    entityId: commissionId,
    action: 'UPDATE',
    fieldName: 'payment',
    oldValue: 'deleted',
    newValue: '',
  });
  return { error: null };
}

// Devuelve cuántas comisiones pendientes (y su monto total) tiene el vendedor
// vinculado a un email. Se usa al borrar un usuario para advertir si quedan
// comisiones sin pagar.
export async function getPendingCommissionsByEmail(
  email: string,
): Promise<{ count: number; total: number }> {
  if (!isSupabaseConfigured || !supabase || !email) return { count: 0, total: 0 };
  try {
    const { data: sp } = await supabase
      .from('salespersons')
      .select('id')
      .eq('email', email)
      .maybeSingle();
    if (!sp) return { count: 0, total: 0 };

    const spId = (sp as { id: string }).id;
    const { data: comms } = await supabase
      .from('commissions')
      .select('commission_amount, is_paid')
      .eq('salesperson_id', spId)
      .eq('is_paid', false);
    const list = (comms ?? []) as Array<Record<string, any>>;
    const total = list.reduce((s, c) => s + Number(c.commission_amount ?? 0), 0);
    return { count: list.length, total };
  } catch {
    return { count: 0, total: 0 };
  }
}